import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { Card } from "@masterteam/mt-design-react";
import Wizard from "../../KPIWizard/Wizard";
import TypeIconTab, { typeValidationSchema } from "./TypeIconTab";
import StatusTab, { statusValidationSchema } from "./StatusTab";
import WorkflowTab, { workflowValidationSchema } from "./WorkflowTab";
import EscalationsTab, { escalationValidationSchema } from "./EscalationsTab";
import api from "../../../providers/api";

import { addToast } from "../../../helpers";

import "./style.scss";

const typeInitialData = {
  EnglishName: "",
  ArabicName: "",
  GracePeriod: "",
  Attachment: null,
  BeforeReminder: "",
  FirstReminder: "",
  SecondReminder: "",
  Status: [],
  Workflows: [
    {
      BaseWorkflowID: 1,
      WorkflowSteps: [
        {
          Approver: {
            Arabic: "Champion",
            English: "Champion",
            ID: 1437,
            LookupID: 8077,
            Order0: "1",
            Value: "1",
          },
          ArabicName: "",
          EnglishName: "",
          IsDeletable: false,
          Order: 1,
          isGroup: false,
        },
      ],
    },
  ],
};

function Form({ selectedType, groups, callback }) {
  const intl = useIntl();
  const approvers = useSelector(
    ({ theme }) => theme.WorkflowApprovers.item.Items
  );
  const [data, setData] = useState(typeInitialData);
  const [loading, setLoading] = useState(true);

  async function getTypeDetails(id) {
    try {
      setLoading(true);
      const res = await api.kpiType.getById(id);
      setData({
        ...res?.Data,
        Workflows: res?.Data?.Workflows?.length
          ? res.Data.Workflows.map((item, index) => ({
              ...item,
              WorkflowSteps: res.Data.Workflows[index]?.WorkflowSteps?.map(
                (step) => ({
                  ...step,
                  Approver: step.isGroup
                    ? groups.find((group) => group.ID === step.Approver)
                    : approvers.find(
                        (ap) => parseInt(ap.Value, 10) === step.Approver
                      ),
                })
              ),
            }))
          : typeInitialData.Workflows,
      });
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error",
      });
    } finally {
      setLoading(false);
    }
  }

  async function getStatuses() {
    try {
      setLoading(true);
      const res = await api.kpiType.getCreationStatuses();
      setData((currentData) => ({ ...currentData, Status: res.Data }));
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error",
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (selectedType !== -1) {
      getTypeDetails(selectedType);
    } else {
      getStatuses();
    }
  }, [selectedType]);

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      const body = {
        ...values,
        Workflows: [
          {
            ...values.Workflows[0],
            WorkflowSteps: values.Workflows[0].WorkflowSteps.map(
              (item, index) => ({
                ...item,
                Order: index + 1,
                Approver: parseInt(item.Approver?.Value) || item.Approver?.ID,
              })
            ),
          },
        ],
      };
      if (body.Attachment && !body.Attachment?.ID) {
        const attachments = await api.attachment.create(values.Attachment);
        body.Attachment = { ID: attachments.Data?.[0] };
      }
      const service = values.ID ? "update" : "create";
      const res = await api.kpiType[service](body);
      const { Data, StatusCode } = res;
      if (StatusCode === "Success") {
        addToast.success(
          values.ID ? "toast.successUpdate" : "toast.successSave"
        );
        callback(Data, selectedType);
      }
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "get-users-error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card loading={loading} className="kpi-config__form animated fadeInRight">
      <Wizard initialValues={data} onSubmit={onSubmit}>
        <TypeIconTab
          readOnly={loading}
          validationSchema={typeValidationSchema}
          name={intl.formatMessage({ id: "kpi-type.type-icon" })}
        />
        <StatusTab
          readOnly={loading}
          validationSchema={statusValidationSchema}
          name={intl.formatMessage({ id: "kpi-type.status-thresholds" })}
        />
        <WorkflowTab
          groups={groups}
          readOnly={loading}
          validationSchema={workflowValidationSchema}
          name={intl.formatMessage({ id: "kpi-type.workflow" })}
        />
        <EscalationsTab
          readOnly={loading}
          validationSchema={escalationValidationSchema}
          name={intl.formatMessage({ id: "kpi-type.reminders-escalations" })}
        />
      </Wizard>
    </Card>
  );
}

export default Form;
